/**
 * UserDetail.jsx — Unified Player & Member Dossier Widget ('user-detail').
 *
 * Absorbs:
 *   - user-detail (Member & Seller Summary)
 *   - player-insight (239k PC-NA Player Discovery & External Trader Dossier)
 *   - profile-management (Admin Identity Registry & Role/Relay Governance)
 */
import { createSignal, createEffect, Show, For } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import UserDetailBody from './UserDetailBody.jsx';
import Avatar from '../components/Avatar.jsx';
import { gold, qty } from '../lib/format.js';
import api from '../lib/apiClient.js';
import wm from '../lib/wmStore.js';

const DISCOVERY_PRESETS = [
  { key: 'profiles', label: '🪪 Registered Profiles', adminOnly: true },
  { key: 'flippers', label: '🦈 External Flippers' },
  { key: 'whales',   label: '🐋 Whale Buyers' },
  { key: 'external_sellers', label: '🏪 Non-Redfur Sellers' },
  { key: 'search',   label: '🔎 All 239k Accounts' },
];

export default function UserDetail(props) {
  const widgetType = () => props.widget?.type || 'user-detail';
  const initialTag = String(props.settings?.tag || '').trim();
  const [tag, setTag] = createSignal(initialTag);
  const [input, setInput] = createSignal(initialTag);
  const [weekOffset, setWeekOffset] = createSignal(Math.max(0, Number(props.settings?.weekOffset) || 0));

  // Open directory automatically if launched via legacy player-insight or profile-management slug without a tag
  const defaultDirOpen = !initialTag && (widgetType() === 'player-insight' || widgetType() === 'profile-management');
  const [dirOpen, setDirOpen] = createSignal(defaultDirOpen);
  const [preset, setPreset] = createSignal(
    widgetType() === 'profile-management' ? 'profiles' : (props.settings?.preset || 'flippers')
  );
  const [dirSearch, setDirSearch] = createSignal(props.settings?.search || '');
  const [profileGroup, setProfileGroup] = createSignal(props.settings?.group || 'all');
  const [dirRows, setDirRows] = createSignal([]);
  const [dirTotal, setDirTotal] = createSignal(0);
  const [dirLoading, setDirLoading] = createSignal(false);

  const isAdmin = () => !!wm.state.identity?.isAdmin;

  createEffect(() => {
    const next = String(props.settings?.tag || '').trim();
    if (next !== tag()) {
      setTag(next);
      setInput(next);
    }
  });

  const { data, loading, error, refetch } = useWidgetData('user-detail', () => ({
    tag: isAdmin() ? tag() : (wm.state.identity?.tag || ''),
    spyGuildFilter: props.settings?.spyGuildFilter || 'all',
    weekOffset: weekOffset(),
  }));

  // Fetch directory / cohort list only when drawer is open (staff only)
  createEffect(() => {
    if (!dirOpen() || !isAdmin()) return;
    const activePreset = preset();
    const q = dirSearch().trim();
    const grp = profileGroup();
    setDirLoading(true);

    if (activePreset === 'profiles' && isAdmin()) {
      api.fetchWidget('profile-management', { page: 1, pageSize: 40, search: q, group: grp })
        .then((res) => {
          const payload = res?.payload || {};
          setDirRows(payload.profiles || []);
          setDirTotal(payload.total || (payload.profiles || []).length);
        })
        .catch(() => { setDirRows([]); setDirTotal(0); })
        .finally(() => setDirLoading(false));
    } else {
      const effectivePreset = activePreset === 'profiles' ? 'flippers' : activePreset;
      api.fetchWidget('player-insight', { preset: effectivePreset, q, limit: 35 })
        .then((res) => {
          const list = res?.payload?.discovery || [];
          setDirRows(list);
          setDirTotal(list.length);
        })
        .catch(() => { setDirRows([]); setDirTotal(0); })
        .finally(() => setDirLoading(false));
    }
  });

  function updateWeekOffset(offset) {
    const next = Math.max(0, Number(offset) || 0);
    setWeekOffset(next);
    if (props.widget?.id) {
      try { wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), weekOffset: next }); } catch {}
    }
  }

  function commit(v) {
    const cleaned = String(v || '').trim().replace(/^@+/, '');
    setTag(cleaned);
    setInput(cleaned);
    if (props.widget?.id) {
      try { wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), tag: cleaned }); } catch {}
    }
  }

  function clear() {
    setInput('');
    commit('');
  }

  function selectFromDirectory(targetTag) {
    if (!targetTag) return;
    commit(targetTag);
  }

  return (
    <div class="v2-widget v2-user-detail-widget">
      {/* Non-staff Personal Header (locked to own profile) */}
      <Show when={!isAdmin()}>
        <div class="v2-user-detail__viewing-hint" style={{ display: 'flex', 'align-items': 'center', 'justify-content': 'space-between', gap: '8px' }}>
          <span>👤 Viewing your personal dossier: <b>{wm.state.identity?.tag || data()?.user?.tag || 'Unlinked'}</b></span>
          <span class="v2-badge" title="Inspecting other players is restricted to Redfur staff">🔒 Self-Only</span>
        </div>
      </Show>

      {/* Top Lookup & Directory Toggle Toolbar (Staff Only) */}
      <Show when={isAdmin()}>
      <form
        class="v2-toolbar v2-user-detail__search"
        onSubmit={(e) => { e.preventDefault(); commit(input()); }}
      >
        <input
          class="v2-input"
          type="search"
          autocomplete="off"
          spellcheck={false}
          placeholder="@-tag lookup (leave blank for yourself)…"
          value={input()}
          onInput={(e) => setInput(e.currentTarget.value)}
          style={{ flex: 1, 'min-width': '160px' }}
        />
        <button type="submit" class="v2-btn-sm">🔍 Look up</button>
        <Show when={input()}>
          <button type="button" class="v2-btn-sm" onClick={clear} title="Reset to yourself">✕ Self</button>
        </Show>
        <button
          type="button"
          class={`v2-btn-sm ${dirOpen() ? 'is-active' : ''}`}
          onClick={() => setDirOpen((v) => !v)}
          title="Browse Player Directory, External Flippers, Whales & Registered Profiles"
        >
          📂 Directory {dirOpen() ? '▴' : '▾'}
        </button>
      </form>

      {/* Expandable / Collapsible Player & Member Directory Drawer */}
      <Show when={dirOpen()}>
        <div class="v2-user-detail__directory">
          <div class="v2-tabs" role="tablist" aria-label="Directory cohorts">
            <For each={DISCOVERY_PRESETS.filter((p) => !p.adminOnly || isAdmin())}>{(p) => (
              <button
                type="button"
                role="tab"
                aria-selected={preset() === p.key}
                class={`v2-tab ${preset() === p.key ? 'is-active' : ''}`}
                onClick={() => setPreset(p.key)}
              >
                {p.label}
              </button>
            )}</For>
          </div>
          <div class="v2-toolbar" style={{ 'margin-top': '6px' }}>
            <input
              type="search"
              class="v2-input"
              placeholder={preset() === 'profiles' ? 'Filter profiles by name, @tag, or Discord ID…' : 'Filter cohort by @tag…'}
              value={dirSearch()}
              onInput={(e) => setDirSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Show when={preset() === 'profiles' && isAdmin()}>
              <select
                class="v2-input"
                value={profileGroup()}
                onChange={(e) => setProfileGroup(e.currentTarget.value)}
                aria-label="Filter role group"
              >
                <option value="all">All Roles ({dirTotal()})</option>
                <option value="owner">Owners</option>
                <option value="family">Family</option>
                <option value="staff">Redfur Staff</option>
                <option value="member">Members</option>
              </select>
            </Show>
          </div>

          <Show when={!dirLoading()} fallback={<div class="v2-dim" style={{ padding: '8px', 'font-size': '11px' }}>Scanning accounts…</div>}>
            <div class="v2-user-detail__dir-list">
              <Show when={dirRows().length} fallback={<div class="v2-empty" style={{ padding: '8px' }}>No matching players found in this cohort.</div>}>
                <For each={dirRows()}>{(row) => {
                  const rowTag = () => String(row.atTag || row.tag || row.displayName || '').replace(/^@+/, '');
                  const isCurrent = () => rowTag() && rowTag().toLowerCase() === String(tag() || data()?.user?.tag || '').replace(/^@+/, '').toLowerCase();
                  return (
                    <button
                      type="button"
                      class={`v2-user-detail__dir-item ${isCurrent() ? 'is-selected' : ''}`}
                      onClick={() => selectFromDirectory(rowTag())}
                    >
                      <Show when={row.avatar}>
                        <Avatar url={row.avatar} name={row.displayName || rowTag()} size={22} />
                      </Show>
                      <span class="v2-user-detail__dir-name">@{rowTag() || 'unlinked'}</span>
                      <Show when={row.role}>
                        <span class={`v2-badge v2-badge--${row.role}`}>{row.role}</span>
                      </Show>
                      <Show when={row.affiliation}>
                        <span class="v2-badge">{row.affiliation === 'REDFUR' ? 'Redfur' : row.affiliation === 'SYNCED_GUILD' ? 'Synced' : 'External'}</span>
                      </Show>
                      <span class="v2-user-detail__dir-meta">
                        <Show when={row.boughtVolume > 0}>
                          <span>Bought {gold(row.boughtVolume)} ({qty(row.purchasesCount)})</span>
                        </Show>
                        <Show when={row.soldVolume > 0}>
                          <span>Sold {gold(row.soldVolume)} ({qty(row.salesCount)})</span>
                        </Show>
                        <Show when={row.syncPoints != null}>
                          <span>{qty(row.syncPoints)} pts · {qty(row.mmSyncs || 0)} MM / {qty(row.ttcSyncs || 0)} TTC</span>
                        </Show>
                      </span>
                    </button>
                  );
                }}</For>
              </Show>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={!input() && data()?.user?.tag}>
        <div class="v2-user-detail__viewing-hint">
          Currently viewing: <b>{data()?.user?.tag}</b> — type any @-tag above or open <b>📂 Directory</b> to inspect another player.
        </div>
      </Show>
      </Show>

      <UserDetailBody
        tag={tag() || data()?.user?.tag || ''}
        data={data()}
        loading={loading()}
        error={error()}
        weekOffset={weekOffset()}
        onWeekOffset={updateWeekOffset}
        onRefetch={refetch}
        initialTab={widgetType() === 'profile-management' ? 'tech' : 'sales'}
      />
    </div>
  );
}
